import { useEffect, useMemo, useState } from "react";
import {
    CalendarClock,
    Check,
    Clock3,
    Loader2,
    Mail,
    MessageCircle,
    NotebookPen,
    Phone,
    Video,
    X,
} from "lucide-react";

import axiosInstance from "src/common/axiosInstance";

const activityConfig = {
    FOLLOWUP: {
        title: "Schedule follow-up",
        description: "Set the next touchpoint and how you plan to connect.",
        noteLabel: "Reminder note",
        notePlaceholder: "Add context for the next conversation...",
        submitLabel: "Schedule follow-up",
        icon: CalendarClock,
        iconClass: "bg-blue-50 text-blue-600 ring-blue-100",
        buttonClass: "bg-blue-600 hover:bg-blue-700 focus-visible:ring-blue-500",
        requiresSchedule: true,
    },
    NOTE: {
        title: "Add note",
        description: "Keep important lead context visible to the team.",
        noteLabel: "Note",
        notePlaceholder: "Write a clear update about this lead...",
        submitLabel: "Save note",
        icon: NotebookPen,
        iconClass: "bg-amber-50 text-amber-600 ring-amber-100",
        buttonClass: "bg-amber-600 hover:bg-amber-700 focus-visible:ring-amber-500",
        requiresSchedule: false,
        requiresNote: true,
    },
    MEETING: {
        title: "Schedule meeting",
        description: "Reserve the next conversation with this lead.",
        noteLabel: "Agenda",
        notePlaceholder: "What should be covered in this meeting?",
        submitLabel: "Schedule meeting",
        icon: Video,
        iconClass: "bg-cyan-50 text-cyan-600 ring-cyan-100",
        buttonClass: "bg-cyan-600 hover:bg-cyan-700 focus-visible:ring-cyan-500",
        requiresSchedule: true,
    },
    EMAIL: {
        title: "Log email",
        description: "Record an email touchpoint for a complete lead history.",
        noteLabel: "Email summary",
        notePlaceholder: "Add the subject, outcome, or next step...",
        submitLabel: "Log email",
        icon: Mail,
        iconClass: "bg-rose-50 text-rose-600 ring-rose-100",
        buttonClass: "bg-rose-600 hover:bg-rose-700 focus-visible:ring-rose-500",
        requiresSchedule: false,
    },
};

const followUpMethods = [
    { value: "CALL", label: "Call", icon: Phone },
    { value: "EMAIL", label: "Email", icon: Mail },
    { value: "WHATSAPP", label: "WhatsApp", icon: MessageCircle },
    { value: "MEETING", label: "Meeting", icon: Video },
];

const emailDirections = [
    { value: "OUTBOUND", label: "Sent" },
    { value: "INBOUND", label: "Received" },
];

function toLocalDateTime(date) {
    const pad = (value) => String(value).padStart(2, "0");

    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function nextAvailableTime() {
    const date = new Date();
    date.setHours(date.getHours() + 1);
    date.setMinutes(Math.ceil(date.getMinutes() / 15) * 15, 0, 0);
    return toLocalDateTime(date);
}

function createInitialForm(type) {
    const requiresSchedule = Boolean(activityConfig[type]?.requiresSchedule);

    return {
        status: requiresSchedule ? "PENDING" : "COMPLETED",
        note: "",
        response_type: type === "EMAIL" ? "OUTBOUND" : "CALL",
        scheduled_at: requiresSchedule ? nextAvailableTime() : "",
    };
}

function getRequestError(error) {
    const validationErrors = error.response?.data?.errors;

    if (validationErrors) {
        const firstError = Object.values(validationErrors).flat()[0];
        if (firstError) return firstError;
    }

    return error.response?.data?.message || "We couldn't save this activity. Please try again.";
}

function quickSchedule(label, getDate) {
    return { label, value: () => toLocalDateTime(getDate()) };
}

const scheduleShortcuts = [
    quickSchedule("In 1 hour", () => {
        const date = new Date();
        date.setHours(date.getHours() + 1);
        date.setMinutes(Math.ceil(date.getMinutes() / 15) * 15, 0, 0);
        return date;
    }),
    quickSchedule("Tomorrow", () => {
        const date = new Date();
        date.setDate(date.getDate() + 1);
        date.setHours(10, 0, 0, 0);
        return date;
    }),
    quickSchedule("Next week", () => {
        const date = new Date();
        date.setDate(date.getDate() + 7);
        date.setHours(10, 0, 0, 0);
        return date;
    }),
];

function ChoiceGroup({ label, options, value, onChange }) {
    return (
        <fieldset>
            <legend className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                {label}
            </legend>
            <div className={`grid gap-2 ${options.length > 2 ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-2"}`}>
                {options.map((option) => {
                    const Icon = option.icon;
                    const selected = value === option.value;

                    return (
                        <button
                            key={option.value}
                            type="button"
                            aria-pressed={selected}
                            onClick={() => onChange(option.value)}
                            className={`flex h-10 items-center justify-center gap-2 rounded-lg border px-3 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/30 ${selected
                                ? "border-slate-900 bg-slate-900 text-white shadow-sm"
                                : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50"
                                }`}
                        >
                            {Icon && <Icon className="h-4 w-4" />}
                            {option.label}
                            {selected && <Check className="h-3.5 w-3.5" />}
                        </button>
                    );
                })}
            </div>
        </fieldset>
    );
}

export default function LeadActivityModal({
    leadId,
    phone,
    type: initialType = null,
    onClose,
    onSuccess,
}) {
    const [type, setType] = useState(initialType);
    const [form, setForm] = useState(() => createInitialForm(initialType));
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const config = type ? activityConfig[type] : null;
    const minimumSchedule = useMemo(() => toLocalDateTime(new Date()), []);

    useEffect(() => {
        if (initialType !== "CALL") return;

        if (phone) window.location.href = `tel:${phone}`;
        onClose();
    }, [initialType, phone, onClose]);

    if (initialType === "CALL") return null;

    const updateForm = (field, value) => {
        setError("");
        setForm((current) => ({ ...current, [field]: value }));
    };

    const selectType = (nextType) => {
        setType(nextType);
        setForm(createInitialForm(nextType));
        setError("");
    };

    const closeModal = () => {
        if (!loading) onClose();
    };

    const handleKeyDown = (event) => {
        event.stopPropagation();
        if (event.key === "Escape") closeModal();
    };

    async function submit(event) {
        event.preventDefault();

        if (config.requiresSchedule && (!form.scheduled_at || Number.isNaN(new Date(form.scheduled_at).getTime()))) {
            setError("Choose a date and time before scheduling this activity.");
            return;
        }

        if (config.requiresNote && !form.note.trim()) {
            setError("Add a note before saving.");
            return;
        }

        try {
            setLoading(true);
            setError("");

            await axiosInstance.post("/lead-activities", {
                lead_id: leadId,
                type,
                status: form.status,
                response_type:
                    type === "FOLLOWUP" || type === "EMAIL"
                        ? form.response_type
                        : null,
                note: form.note.trim() || null,
                // datetime-local is the browser clock; send an unambiguous UTC instant.
                scheduled_at: config.requiresSchedule ? new Date(form.scheduled_at).toISOString() : null,
            });

            onSuccess?.();
        } catch (requestError) {
            console.error(requestError);
            setError(getRequestError(requestError));
        } finally {
            setLoading(false);
        }
    }

    if (!type) {
        return (
            <div
                className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-[2px]"
                role="dialog"
                aria-modal="true"
                aria-labelledby="activity-picker-title"
                tabIndex={-1}
                autoFocus
                onMouseDown={(event) => event.target === event.currentTarget && closeModal()}
                onKeyDown={handleKeyDown}
            >
                <div className="w-full max-w-lg overflow-hidden rounded-2xl border border-white/60 bg-white shadow-2xl">
                    <div className="flex items-start justify-between border-b border-slate-100 px-5 py-4">
                        <div>
                            <h2 id="activity-picker-title" className="text-lg font-bold text-slate-950">
                                Add activity
                            </h2>
                            <p className="mt-0.5 text-sm text-slate-500">Choose what you want to record.</p>
                        </div>
                        <button
                            type="button"
                            onClick={closeModal}
                            aria-label="Close activity modal"
                            className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>

                    <div className="grid grid-cols-2 gap-2 p-4">
                        {Object.entries(activityConfig).map(([key, item]) => {
                            const Icon = item.icon;

                            return (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => selectType(key)}
                                    className="group rounded-xl border border-slate-200 p-4 text-left transition hover:border-slate-300 hover:bg-slate-50 hover:shadow-sm"
                                >
                                    <span className={`flex h-9 w-9 items-center justify-center rounded-lg ring-1 ${item.iconClass}`}>
                                        <Icon className="h-[18px] w-[18px]" />
                                    </span>
                                    <span className="mt-3 block text-sm font-bold text-slate-900">{item.title}</span>
                                    <span className="mt-1 block text-xs leading-5 text-slate-500">{item.description}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    }

    const ActivityIcon = config.icon;

    return (
        <div
            className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/45 p-3 backdrop-blur-[2px] sm:p-5"
            role="dialog"
            aria-modal="true"
            aria-labelledby="activity-modal-title"
            tabIndex={-1}
            autoFocus
            onMouseDown={(event) => event.target === event.currentTarget && closeModal()}
            onKeyDown={handleKeyDown}
        >
            <div className="flex max-h-[calc(100vh-1.5rem)] w-full max-w-[520px] flex-col overflow-hidden rounded-2xl border border-white/70 bg-white shadow-[0_24px_80px_-20px_rgba(15,23,42,0.5)] sm:max-h-[calc(100vh-2.5rem)]">
                <header className="flex shrink-0 items-start gap-3 border-b border-slate-100 px-5 py-4 sm:px-6">
                    <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ring-1 ${config.iconClass}`}>
                        <ActivityIcon className="h-5 w-5" />
                    </span>
                    <div className="min-w-0 flex-1">
                        <h2 id="activity-modal-title" className="text-lg font-bold tracking-tight text-slate-950">
                            {config.title}
                        </h2>
                        <p className="mt-0.5 text-sm leading-5 text-slate-500">{config.description}</p>
                    </div>
                    <button
                        type="button"
                        onClick={closeModal}
                        disabled={loading}
                        aria-label="Close activity modal"
                        className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </header>

                <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col" noValidate>
                    <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-5 sm:px-6">
                        {type === "FOLLOWUP" && (
                            <ChoiceGroup
                                label="Follow up by"
                                options={followUpMethods}
                                value={form.response_type}
                                onChange={(value) => updateForm("response_type", value)}
                            />
                        )}

                        {type === "EMAIL" && (
                            <ChoiceGroup
                                label="Email direction"
                                options={emailDirections}
                                value={form.response_type}
                                onChange={(value) => updateForm("response_type", value)}
                            />
                        )}

                        {config.requiresSchedule && (
                            <div>
                                <div className="mb-2 flex items-center justify-between gap-3">
                                    <label htmlFor="activity-schedule" className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                                        Date and time
                                    </label>
                                    <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-400">
                                        <Clock3 className="h-3.5 w-3.5" /> {Intl.DateTimeFormat().resolvedOptions().timeZone}
                                    </span>
                                </div>
                                <div className="relative">
                                    <CalendarClock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                                    <input
                                        id="activity-schedule"
                                        type="datetime-local"
                                        required
                                        min={minimumSchedule}
                                        value={form.scheduled_at}
                                        onChange={(event) => updateForm("scheduled_at", event.target.value)}
                                        className="h-11 w-full rounded-xl border border-slate-200 bg-white pl-10 pr-3 text-sm font-semibold text-slate-800 outline-none transition hover:border-slate-300 focus:border-blue-400 focus:ring-4 focus:ring-blue-50"
                                    />
                                </div>
                                <div className="mt-2 flex flex-wrap gap-1.5">
                                    {scheduleShortcuts.map((shortcut) => (
                                        <button
                                            key={shortcut.label}
                                            type="button"
                                            onClick={() => updateForm("scheduled_at", shortcut.value())}
                                            className="rounded-md bg-slate-100 px-2.5 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-200 hover:text-slate-900"
                                        >
                                            {shortcut.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div>
                            <div className="mb-2 flex items-center justify-between gap-3">
                                <label htmlFor="activity-note" className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                                    {config.noteLabel}
                                    {config.requiresNote && <span className="text-rose-500"> *</span>}
                                </label>
                                <span className="text-[11px] font-medium text-slate-400">{form.note.length} characters</span>
                            </div>
                            <textarea
                                id="activity-note"
                                rows={type === "NOTE" ? 7 : 4}
                                value={form.note}
                                onChange={(event) => updateForm("note", event.target.value)}
                                placeholder={config.notePlaceholder}
                                autoFocus={type === "NOTE"}
                                className="w-full resize-none rounded-xl border border-slate-200 bg-slate-50/50 px-3.5 py-3 text-sm leading-6 text-slate-900 outline-none transition placeholder:text-slate-400 hover:border-slate-300 focus:border-blue-400 focus:bg-white focus:ring-4 focus:ring-blue-50"
                            />
                        </div>

                        {error && (
                            <div role="alert" className="rounded-xl border border-rose-200 bg-rose-50 px-3.5 py-3 text-sm font-medium text-rose-700">
                                {error}
                            </div>
                        )}
                    </div>

                    <footer className="flex shrink-0 items-center justify-end gap-2 border-t border-slate-100 bg-slate-50/70 px-5 py-3 sm:px-6">
                        <button
                            type="button"
                            onClick={closeModal}
                            disabled={loading}
                            className="h-10 rounded-lg px-4 text-sm font-semibold text-slate-600 transition hover:bg-slate-200/70 hover:text-slate-900 disabled:opacity-40"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className={`inline-flex h-10 min-w-[132px] items-center justify-center gap-2 rounded-lg px-4 text-sm font-bold text-white shadow-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-55 ${config.buttonClass}`}
                        >
                            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                            {loading ? "Saving..." : config.submitLabel}
                        </button>
                    </footer>
                </form>
            </div>
        </div>
    );
}
